import { TurnFieldProposal } from "../../../util/slopifyThread";
import { prettyLabel } from "../../DynamicField";

/**
 * Rendering kind for a diff group. `"text"` falls back to the plain scalar
 * BEFORE/AFTER text pane (FieldDiffRow); the others render a structured,
 * read-only preview that mirrors the node-form bespoke quizz editors.
 */
export type DiffGroupKind =
  | "text"
  | "quizz3Answers"
  | "imageAnswers"
  | "sortAnswers"
  | "randomQuestions";

export interface DiffGroup {
  /** Stable key for React + the checkbox/apply set. */
  id: string;
  label: string;
  kind: DiffGroupKind;
  /** The TurnFieldProposal(s) folded into this group. */
  members: TurnFieldProposal[];
  /** Field names this group owns — toggling the group checks/unchecks all of them. */
  fieldNames: string[];
  /** True when every member is unchanged. */
  unchanged: boolean;
}

interface ArrayGroupConfig {
  kind: DiffGroupKind;
  /** Top-level generatedData fields that fuse into one structured card. */
  fields: string[];
  /** i18n leaf under general.assistantScenarIa.diffGroups.* */
  labelKey: "answers" | "questions";
}

/**
 * Which JSON-array fields fuse into one structured preview, per content type.
 * Mirrors the node-form editors: quizz-3 pairs list_answers + correct_answers;
 * the object-array types each own a single field.
 */
const ARRAY_GROUP_BY_MODEL: Record<string, ArrayGroupConfig> = {
  "content-quizz-3": {
    kind: "quizz3Answers",
    fields: ["list_answers", "correct_answers"],
    labelKey: "answers",
  },
  "content-quizz-image-multiple": {
    kind: "imageAnswers",
    fields: ["list_answers"],
    labelKey: "answers",
  },
  "content-quizz-sort": {
    kind: "sortAnswers",
    fields: ["answers"],
    labelKey: "answers",
  },
  "content-random-quizz": {
    kind: "randomQuestions",
    fields: ["questions"],
    labelKey: "questions",
  },
};

/**
 * Fold the flat list of field proposals into render groups: the quizz array
 * field(s) collapse into one structured group (positioned where the first
 * member field appears in the original order); every other field stays its own
 * `text` group. Member field order is preserved everywhere else.
 */
export const buildDiffGroups = (
  modelName: string,
  fields: TurnFieldProposal[],
  t: (key: string, opts?: any) => string,
): DiffGroup[] => {
  const cfg = ARRAY_GROUP_BY_MODEL[modelName];
  const groupFields = new Set(cfg?.fields ?? []);
  const groups: DiffGroup[] = [];
  let arrayGroupInserted = false;

  for (const field of fields) {
    if (cfg && groupFields.has(field.fieldName)) {
      if (arrayGroupInserted) continue; // members render once, in the fused card
      const members = fields.filter((f) => groupFields.has(f.fieldName));
      groups.push({
        id: `group:${cfg.kind}`,
        label: t(`general.assistantScenarIa.diffGroups.${cfg.labelKey}`),
        kind: cfg.kind,
        members,
        fieldNames: members.map((m) => m.fieldName),
        unchanged: members.every((m) => m.unchanged),
      });
      arrayGroupInserted = true;
      continue;
    }
    groups.push({
      id: `field:${field.fieldName}`,
      label: prettyLabel(field.fieldName, t),
      kind: "text",
      members: [field],
      fieldNames: [field.fieldName],
      unchanged: field.unchanged,
    });
  }

  return groups;
};
